import { useRef, useMemo, useEffect } from 'react';
import { useEventSource } from './useEventSource';
/**
 * SSE stream for order events from Station API.
 * Writes to store.orderData. Accumulates transactions from order_event messages.
 */
export function useOrderStream(options) {
    const { baseUrl, solverAddress, hashlock, enabled, store, onFailed } = options;
    const onFailedRef = useRef(onFailed);
    onFailedRef.current = onFailed;
    const accumulatedTxsRef = useRef([]);
    // Reset accumulated state when stream params change (new swap)
    useEffect(() => {
        accumulatedTxsRef.current = [];
    }, [solverAddress, hashlock]);
    const url = hashlock
        ? `${baseUrl}/api/v1/orders/${encodeURIComponent(hashlock)}/stream${solverAddress ? `?solverAddress=${encodeURIComponent(solverAddress)}` : ''}`
        : null;
    const eventHandlers = useMemo(() => ({
        order: (data) => {
            if (!hashlock)
                return;
            const response = data;
            const order = response.order;
            const merged = {
                ...order,
                transactions: [
                    ...(order.transactions ?? []),
                    ...accumulatedTxsRef.current,
                ],
            };
            store?.getState().setOrderData(hashlock, merged);
        },
        order_event: (data) => {
            if (!hashlock)
                return;
            const event = data;
            if (event.eventType === 'order.transaction_created') {
                const txData = event.data;
                const tx = {
                    type: txData.transactionType,
                    hash: txData.transactionHash,
                    network: txData.network,
                };
                accumulatedTxsRef.current = [...accumulatedTxsRef.current, tx];
                const currentOrder = store?.getState().orderData[hashlock];
                if (currentOrder) {
                    store?.getState().setOrderData(hashlock, {
                        ...currentOrder,
                        transactions: [...(currentOrder.transactions ?? []), tx],
                    });
                }
            }
            else if (event.eventType === 'order.status_changed') {
                const statusData = event.data;
                if (statusData.status === 'failed') {
                    onFailedRef.current?.(statusData.failureReason ?? 'Please wait for the timelock to expire, then refund to receive your assets back.');
                }
            }
        },
        done: (_data) => {
            return 'close';
        },
    }), [store, hashlock]);
    useEventSource(url, {
        enabled: enabled && !!url,
        onEvent: eventHandlers,
    });
}
